import { Avatar } from "@/components/ui/Avatar";
import { useTranslations } from "@/contexts/I18nContext";
import type { MemberProfile } from "@/lib/models";

const REACTION_EMOJIS: Record<string, string> = {
  love: "❤️",
  haha: "😂",
  celebrate: "🎉",
  wow: "😮",
  sad: "😢",
  fire: "🔥",
};

interface ReactionDetailsSheetProps {
  reactions: Record<string, string[]> | undefined;
  members: MemberProfile[];
  onClose: () => void;
}

/**
 * Bottom-sheet listing every member who reacted, grouped by emoji.
 * Mirrors iOS PostView/ChatView reaction-detail sheet (long-press a
 * reaction pill).
 */
export function ReactionDetailsSheet({
  reactions,
  members,
  onClose,
}: ReactionDetailsSheetProps) {
  const t = useTranslations();
  const memberById = new Map<string, MemberProfile>();
  for (const m of members) {
    if (m.uid) memberById.set(m.uid, m);
  }

  const groups = Object.entries(reactions ?? {})
    .filter(([, uids]) => uids.length > 0)
    .map(([key, uids]) => ({
      key,
      emoji: REACTION_EMOJIS[key] ?? key,
      members: uids
        .map((id) => memberById.get(id))
        .filter((m): m is MemberProfile => Boolean(m)),
    }));

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full sm:max-w-md bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden text-gray-900 dark:text-gray-100 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-white/10">
          <h2 className="font-bold text-base">
            {t("posts.reactions.detailsTitle")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-blue-500 text-sm font-medium"
          >
            {t("common.close")}
          </button>
        </div>
        {groups.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">
            {t("posts.reactions.detailsEmpty")}
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-white/10">
            {groups.map((g) => (
              <div key={g.key} className="p-4">
                <div className="flex items-center gap-2 mb-2 text-sm font-semibold">
                  <span className="text-lg">{g.emoji}</span>
                  <span>{g.members.length}</span>
                </div>
                <div className="space-y-2">
                  {g.members.map((m) => (
                    <div key={m.uid} className="flex items-center gap-2">
                      <Avatar
                        src={m.profile_url}
                        alt={m.nickname ?? m.name ?? ""}
                        size={28}
                      />
                      <span className="text-sm">
                        {m.nickname ?? m.name ?? ""}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
